import { useRangeMode, type RangeMode } from "../context/RangeModeContext";
import type { ScenarioStatus } from "../domain/scenarios";

interface RangeModeToggleProps {
  /** Sweep status from the run/overview `scenarios` block. */
  status?: ScenarioStatus | null;
  /** Called when the user asks to run (or re-run) the sensitivity sweep. */
  onRunSweep?: () => void;
  /** True while a sweep-enqueue request is in flight. */
  sweeping?: boolean;
}

const OPTIONS: { value: RangeMode; label: string; title: string }[] = [
  {
    value: "central",
    label: "Central",
    title: "Single realized value everywhere (default)",
  },
  {
    value: "range",
    label: "Range",
    title: "Reveal the Floor / Central / Ceiling sensitivity bracket",
  },
];

/**
 * Central ⇄ Range segmented control, mirroring ImpactModeToggle.
 *
 * When the sweep hasn't completed, selecting "Range" doesn't flip the view —
 * it prompts (or triggers) the sweep instead. A trailing hint communicates
 * running / stale / not-run states.
 */
export function RangeModeToggle({ status, onRunSweep, sweeping }: RangeModeToggleProps) {
  const { mode, setMode } = useRangeMode();
  const complete = status === "complete";
  const stale = status === "stale";
  const running = status === "running";
  // Range is viewable when bands exist (complete or stale).
  const rangeViewable = complete || stale;

  const handleClick = (value: RangeMode) => {
    if (value === "central") {
      setMode("central");
      return;
    }
    // value === "range"
    if (rangeViewable) {
      setMode("range");
      return;
    }
    // No usable bands yet — trigger the sweep rather than switching.
    if (!running && onRunSweep) onRunSweep();
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center bg-surface-600 rounded-md p-0.5">
        {OPTIONS.map((opt) => {
          const active = mode === opt.value;
          const isRange = opt.value === "range";
          const dimRange = isRange && !rangeViewable;
          return (
            <button
              key={opt.value}
              onClick={() => handleClick(opt.value)}
              title={
                dimRange
                  ? running
                    ? "Sensitivity sweep is running…"
                    : "Run the sensitivity sweep to enable Range"
                  : opt.title
              }
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                active
                  ? "bg-surface-500 text-text-primary shadow-sm"
                  : dimRange
                  ? "text-text-muted/60 hover:text-text-secondary"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Trailing status hint */}
      {sweeping ? (
        <span className="text-[11px] text-text-muted">Enqueuing…</span>
      ) : running ? (
        <span className="inline-flex items-center gap-1 text-[11px] text-quantum-400">
          <span className="h-1.5 w-1.5 rounded-full bg-quantum-400 animate-pulse" />
          Sweep running
        </span>
      ) : !rangeViewable && onRunSweep ? (
        <button
          onClick={onRunSweep}
          className="text-[11px] text-quantum-400 hover:text-quantum-300 underline underline-offset-2"
        >
          Run sensitivity
        </button>
      ) : stale && mode === "range" ? (
        <span
          className="text-[11px] text-amber-400"
          title="The model or config moved since these bands were computed — re-run to refresh."
        >
          stale
          {onRunSweep && (
            <button
              onClick={onRunSweep}
              className="ml-1 text-quantum-400 hover:text-quantum-300 underline underline-offset-2"
            >
              re-run
            </button>
          )}
        </span>
      ) : null}
    </div>
  );
}
